import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/** Prepend a scheme so bare domains still open correctly. */
export function hrefFor(url) {
  const u = (url || "").trim();
  if (!u) return null;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(u) ? u : `https://${u}`;
}

/**
 * View / edit a single task's full name and link.
 *
 * Read-only for non-admins (they can still read the full name and follow the
 * link). Admins get editable fields with a Save button.
 */
export default function TaskDetailModal({ open, task, canEdit, onClose, onSave }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open && task) {
      setName(task.name ?? "");
      setUrl(task.url ?? "");
      setErr(null);
      setBusy(false);
    }
  }, [open, task]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open || !task) return null;

  const href = hrefFor(url);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErr("Task name can't be empty.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const updates = [];
      if (trimmedName !== (task.name ?? "")) updates.push(["name", trimmedName]);
      const trimmedUrl = url.trim();
      if (trimmedUrl !== (task.url ?? "")) updates.push(["url", trimmedUrl || null]);
      for (const [k, v] of updates) {
        await onSave(task.id, k, v);
      }
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
        role="dialog"
        aria-modal="true"
      >
        <h3>{canEdit ? "Edit task" : "Task details"}</h3>

        <div style={{ marginTop: 16 }}>
          <label className="field">Task name</label>
          {canEdit ? (
            <textarea
              value={name}
              onChange={(e) => setName(e.target.value)}
              rows={3}
              autoFocus
              style={{ lineHeight: 1.45 }}
            />
          ) : (
            <p style={{ margin: "4px 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {task.name}
            </p>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <label className="field">Link</label>
          {canEdit ? (
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              spellCheck={false}
              autoComplete="off"
            />
          ) : href ? (
            <p style={{ margin: "4px 0 0", wordBreak: "break-all" }}>
              <a href={href} target="_blank" rel="noopener noreferrer">{url}</a>
            </p>
          ) : (
            <p className="muted" style={{ margin: "4px 0 0" }}>No link.</p>
          )}
          {canEdit && href && (
            <div className="muted-sm" style={{ marginTop: 6, wordBreak: "break-all" }}>
              Opens: <a href={href} target="_blank" rel="noopener noreferrer">{href}</a>
            </div>
          )}
        </div>

        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy}>
            {canEdit ? "Cancel" : "Close"}
          </button>
          {canEdit && (
            <button type="button" className="primary" onClick={handleSave} disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
